import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@mui/icons-material/Edit';
import * as React from 'react';
import { Link } from 'react-router-dom';
import type { Member } from '../actions/getMembers';
import { ReactComponent as DownloadIcon } from '../icons/download.svg';
import { ReactComponent as MemberIcon } from '../icons/member.svg';
import KitProgress from './KitProgress';
import ReportsAndRawDataModal from './ReportsAndRawDataModal';

interface MemberProps {
    member: Member;
    isAdmin?: boolean;
    onEditMember?: (member: Member) => void;
}

const MemberCard = ({ member, isAdmin = false, onEditMember }: MemberProps) => {
    const classes = useStyles();
    const [kitProgressOpen, setKitProgressOpen] = React.useState(false);
    const [reportsAndRawDataOpen, setReportsAndRawDataOpen] =
        React.useState(false);

    return (
        <>
            <div className={classes.card}>
                <div className={classes.frame128}>
                    <Link
                        to={`/member/${member.MemberId}`}
                        className={classes.frame69}
                        state={{ isAdmin, member }}
                    >
                        <MemberIcon />
                        <div className={classes.frame68}>
                            <div className={classes.frame67}>
                                <div className={classes.name}>
                                    {member.FirstName} {member.LastName}
                                </div>
                                <div className={classes.patientId}>
                                    Patient ID: {member.PatientId}
                                </div>
                            </div>
                            <div className={classes.recentUpdate}>
                                Recent update:{' '}
                                {member.InsertionDate
                                    ? new Date(
                                          member.InsertionDate
                                      ).toLocaleDateString()
                                    : 'N/A'}
                            </div>
                        </div>
                    </Link>
                    <div className={classes.frame132}>
                        <div className={classes.frame129}>
                            <div className={classes.frame210}>
                                <div className={classes.frame211}>
                                    <div className={classes.frame209}>
                                        <div className={classes.frame130}>
                                            <div
                                                className={
                                                    classes.completedTests
                                                }
                                            >
                                                Completed Tests
                                            </div>
                                            <div
                                                className={
                                                    classes.completedTestsNumber
                                                }
                                            >
                                                {member.ReadyKitCount}
                                            </div>
                                        </div>
                                        <div className={classes.frame130}>
                                            <div
                                                className={
                                                    classes.completedTests
                                                }
                                            >
                                                Pending Tests
                                            </div>
                                            <div
                                                className={
                                                    classes.completedTestsNumber
                                                }
                                            >
                                                {member.InProgressKitCount}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={classes.frame212}>
                            <div className={classes.frame214}>
                                <div className={classes.divider}>
                                    <svg
                                        width="100%"
                                        height="6"
                                        viewBox="0 0 100 6"
                                        preserveAspectRatio="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <line
                                            x1="0"
                                            y1="3"
                                            x2="100"
                                            y2="3"
                                            stroke="#CDE3FF"
                                            strokeWidth="5"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </div>
                                <div className={classes.frame133}>
                                    <div className={classes.frame126}>
                                        {!isAdmin && (
                                            <div
                                                className={classes.viewProgress}
                                                onClick={() =>
                                                    setKitProgressOpen(true)
                                                }
                                            >
                                                View Progress
                                            </div>
                                        )}
                                        <Button
                                            startIcon={
                                                <DownloadIcon
                                                    className={
                                                        classes.downloadIcon
                                                    }
                                                />
                                            }
                                            className={classes.reportsButton}
                                            onClick={() =>
                                                setReportsAndRawDataOpen(true)
                                            }
                                        >
                                            Reports & Raw Data
                                        </Button>
                                        {isAdmin && (
                                            <Button
                                                startIcon={<EditIcon />}
                                                style={{
                                                    display: 'flex',
                                                    height: 32,
                                                    padding: '10px 14px',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    gap: 13,
                                                    borderRadius: 8,
                                                    backgroundColor: '#D2EBD8',
                                                    color: '#19255B',
                                                    fontFamily: 'Noto Sans',
                                                    fontSize: 14,
                                                    fontStyle: 'normal',
                                                    fontWeight: 600,
                                                    lineHeight: '150%',
                                                    letterSpacing: -0.252,
                                                    textTransform: 'none', // Prevent capitalization
                                                }}
                                                onClick={() =>
                                                    onEditMember?.(member)
                                                }
                                            >
                                                Edit
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <KitProgress
                open={kitProgressOpen}
                onClose={() => setKitProgressOpen(false)}
                memberId={member.MemberId}
                isAdmin={isAdmin}
            />
            <ReportsAndRawDataModal
                open={reportsAndRawDataOpen}
                onClose={() => setReportsAndRawDataOpen(false)}
                member={member}
                isAdmin={isAdmin}
            />
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    card: {
        borderRadius: 12,
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        padding: 20,
        justifyContent: 'space-between',
        rowGap: 18,
        width: '100%', // grid will handle width
        boxSizing: 'border-box',
    },
    frame128: {
        display: 'flex',
        //height: 194,
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        flex: '1 0 0',
    },
    frame69: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 17,
        alignSelf: 'stretch',
        color: 'inherit',
        textDecoration: 'none',
    },
    profilePicture: {
        width: 61,
        height: 61,
    },
    frame68: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
        flex: '1 0 0',
    },
    frame67: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
        alignSelf: 'stretch',
    },
    name: {
        alignSelf: 'stretch',
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: 22,
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: '100%' /* 22px */,
        letterSpacing: -0.396,
        width: '100%',
        margin: 0,
    },
    patientId: {
        alignSelf: 'stretch',
        color: theme.palette.primary.main,
        fontSize: 16,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '150%' /* 24px */,
        width: '100%',
        height: 24,
        margin: 0,
    },
    recentUpdate: {
        alignSelf: 'stretch',
        color: '#9295A9',
        fontFamily: 'Noto Sans',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '150%', // 21px
        letterSpacing: '-0.252px',
    },
    frame132: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 6,
        alignSelf: 'stretch',
    },
    frame129: {
        display: 'flex',
        height: 59,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 9,
        alignSelf: 'stretch',
    },
    frame210: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginTop: '10px',
        gap: '10px',
        width: '100%',
    },
    frame211: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '9px',
        alignSelf: 'stretch',
    },
    frame209: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '2px',
        alignSelf: 'stretch',
    },
    frame130: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'stretch',
    },
    completedTests: {
        width: 220,
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '150%', // 21px
    },
    completedTestsNumber: {
        width: 93,
        color: theme.palette.primary.main,
        textAlign: 'right',
        fontFamily: 'Noto Sans',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: '150%', // 21px
        letterSpacing: '-0.252px',
    },
    frame131: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'stretch',
    },
    frame212: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        alignSelf: 'stretch',
    },
    frame214: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '12px',
        alignSelf: 'stretch',
    },
    divider: {
        height: '6px',
        strokeWidth: '5px',
        stroke: '#CDE3FF',
        width: '100%',
    },
    frame133: {
        display: 'flex',
        height: '32px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '12px',
        alignSelf: 'stretch',
        marginTop: 20,
    },
    frame126: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'stretch',
    },
    reportsButton: {
        display: 'flex',
        height: 32,
        padding: '10px 14px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10,
        borderRadius: 25,
        backgroundColor: theme.palette.gray.light,
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%' /* 21px */,
        letterSpacing: -0.252,
        textTransform: 'none', // Prevent capitalization
    },
    downloadIcon: {
        color: theme.palette.primary.main,
        scale: '0.8',
    },
    viewProgress: {
        display: 'flex',
        padding: '4px 6px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        color: theme.palette.gray.dark,
        fontFamily: 'Noto Sans',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '150%', // or '21px'
        letterSpacing: '-0.252px',
        textDecorationLine: 'underline',
        textDecorationStyle: 'solid',
        textDecorationSkip: 'ink',
        textDecorationThickness: 'auto',
        textUnderlineOffset: 'auto',
        textUnderlinePosition: 'from-font',
        cursor: 'pointer',
    },
}));

export default MemberCard;
